import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  UtensilsCrossed, Bike, ShoppingBasket, CarTaxiFront, Car, Package,
  Wine, Gift, MapPinned, Truck, ChevronRight, Plus, X,
} from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { useI18n } from "@/context/I18nContext";
import LocationChip from "@/components/LocationChip";
import OffersStrip from "@/components/OffersStrip";

/**
 * /services — Slice I · Grab-style Superapp home.
 *
 * A single grid of citizen entry points. Each tile deep-links into an
 * existing Find2Near flow — nothing here creates a duplicate feature.
 * Providers reach their side via `/provider/shift` (linked in the footer).
 */
const TILES = [
  { key: "food",     Icon: UtensilsCrossed, label: "Food",     to: "/search?category=food",       tint: "from-orange-100 to-orange-200",   fg: "text-orange-700" },
  { key: "bike",     Icon: Bike,            label: "Bike",     to: "/search?category=cab&mode=bike", tint: "from-emerald-100 to-emerald-200", fg: "text-emerald-700" },
  { key: "mart",     Icon: ShoppingBasket,  label: "Mart",     to: "/search?category=shopping",    tint: "from-lime-100 to-lime-200",       fg: "text-lime-700"    },
  { key: "ride",     Icon: CarTaxiFront,    label: "Ride",     to: "/search?category=cab",         tint: "from-sky-100 to-sky-200",         fg: "text-sky-700"     },
  { key: "car",      Icon: Car,             label: "Car",      to: "/search?category=cab&mode=car",tint: "from-blue-100 to-blue-200",       fg: "text-blue-700"    },
  { key: "express",  Icon: Package,         label: "Express",  to: "/parcels",                     tint: "from-amber-100 to-amber-200",     fg: "text-amber-700"   },
  { key: "dine_out", Icon: Wine,            label: "Dine Out", to: "/search?category=restaurant",  tint: "from-rose-100 to-rose-200",       fg: "text-rose-700"    },
  { key: "gifts",    Icon: Gift,            label: "Gifts",    to: "/search?category=shopping&q=gift", tint: "from-fuchsia-100 to-fuchsia-200", fg: "text-fuchsia-700" },
  { key: "map",      Icon: MapPinned,       label: "Map",      to: "/venues",                      tint: "from-stone-100 to-stone-200",     fg: "text-stone-700"   },
];

export default function Superapp() {
  const { t } = useI18n();
  useSeo({
    title: "Services — Find2Near",
    description: "Food, ride, mart, express delivery, dine-out and more — one app, one Find2Near ID.",
  });

  return (
    <div className="min-h-screen bg-[#F7FAF8]" data-testid="superapp">
      {/* Green banner à la Grab */}
      <div className="bg-[#0F5132] text-white px-4 pt-6 pb-8 rounded-b-3xl shadow-sm">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-widest text-emerald-200 font-bold">Find2Near</div>
              <div className="heading text-2xl font-black">Every service. One tap.</div>
            </div>
            <LocationChip compact />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 -mt-6 pb-16">
        {/* Tile grid */}
        <div className="bg-white border border-stone-200 rounded-2xl p-4 shadow-sm" data-testid="superapp-grid">
          <div className="grid grid-cols-4 md:grid-cols-5 gap-3">
            {TILES.map(({ key, Icon, label, to, tint, fg }) => (
              <Link
                key={key}
                to={to}
                className="flex flex-col items-center gap-2 group"
                data-testid={`tile-${key}`}
              >
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${tint} grid place-items-center shadow-inner group-hover:scale-105 transition-transform`}>
                  <Icon size={28} className={fg} />
                </div>
                <div className="text-xs font-bold text-stone-800">{t(`superapp.${key}`, label)}</div>
              </Link>
            ))}
          </div>
        </div>

        {/* Offers strip — reuses existing SmartBuy carousel */}
        <div className="mt-4">
          <OffersStrip scope="superapp" className="!p-0" />
        </div>

        {/* Provider CTA */}
        <Link
          to="/provider/shift"
          className="mt-4 flex items-center gap-3 bg-white border border-stone-200 rounded-2xl p-4 hover:border-stone-300"
          data-testid="superapp-provider-cta"
        >
          <div className="w-11 h-11 rounded-full bg-emerald-100 grid place-items-center">
            <Truck size={20} className="text-emerald-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-stone-900">Earn on Find2Near</div>
            <div className="text-xs text-stone-500">Go online, accept nearby requests, get paid.</div>
          </div>
          <ChevronRight size={18} className="text-stone-400" />
        </Link>

        {/* Fare estimator */}
        <FareEstimator />

        {/* Wallet top-up nudge for imminent scheduled trips */}
        <TopupNudge />

        {/* Weekly trip summary — opens automatically on ?summary=1 */}
        <WeeklySummary />

        {/* Recurring rides */}
        <RecurringRides />

        {/* Active delivery peek */}
        <div className="mt-4">
          <ActiveDeliveryCard />
        </div>
      </div>
    </div>
  );
}


import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PinDragMap from "@/components/PinDragMap";
import FindPayCheckout from "@/components/FindPayCheckout";

function FareEstimator() {
  const [service, setService] = useState("ride");
  const [open, setOpen]       = useState(false);
  return (
    <div className="mt-4" data-testid="fare-estimator">
      <div className="bg-white border border-stone-200 rounded-2xl p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500">Book a ride</div>
          <div className="text-[10px] text-stone-400">Powered by Find2Near</div>
        </div>
        <div className="flex gap-1.5 mb-3 flex-wrap" data-testid="fare-services">
          {["ride","car","bike","express","food","mart","gifts"].map(s => (
            <button
              key={s}
              onClick={() => setService(s)}
              className={`text-xs font-bold px-3 py-1.5 rounded-full border capitalize ${service === s ? "bg-[#0F5132] text-white border-[#0F5132]" : "bg-white border-stone-200 text-stone-700"}`}
              data-testid={`fare-svc-${s}`}
            >{s}</button>
          ))}
        </div>
        <button
          onClick={() => setOpen(true)}
          className="w-full rounded-xl bg-gradient-to-r from-[#0F5132] to-[#146c40] text-white font-bold py-3"
          data-testid="fare-open"
        >
          Where to?
        </button>
      </div>
      {open && <RideBookingSheet service={service} onClose={() => setOpen(false)} />}
    </div>
  );
}

function RideBookingSheet({ service, onClose }) {
  const [selectedSvc, setSelectedSvc] = useState(service);   // Slice C1 · user can swap vehicle inside the sheet
  const [pickup, setPickup]     = useState(null);   // {label, lat, lng}
  const [dropoff, setDropoff]   = useState(null);
  const [stops, setStops]       = useState([]);     // [{label,lat,lng}]
  const [quote, setQuote]       = useState(null);
  const [multiQuotes, setMultiQuotes] = useState([]);   // Slice C1 · per-vehicle fare strip
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [payMethod, setPayMethod] = useState(() => {
    try { return localStorage.getItem("f2n_pay_pref") || "upi"; } catch { return "upi"; }
  });
  const [confirming, setConf]   = useState(false);
  const [error, setError]       = useState(null);
  const [saved, setSaved]       = useState([]);
  const [assignTo, setAssignTo] = useState(null);   // "pickup" | "dropoff" | null
  const [scheduleOn, setSchedOn]   = useState(false);
  const [scheduleAt, setSchedAt]   = useState(""); // datetime-local
  const [mapMode, setMapMode]      = useState(null);   // Slice C1 · null | "pickup" | "dropoff"
  // META-1 · FindPay checkout state.
  const [payOpen, setPayOpen]      = useState(false);
  const [platformMid, setPlatformMid] = useState(null);
  useEffect(() => {
    api.get("/findpay/platform_merchant").then(r => setPlatformMid(r.data?.merchant_id))
       .catch(() => setPlatformMid(null));
  }, []);
  const navigate = useNavigate();

  // Slice C1 · which vehicles show up in the carousel. Only render the ones
  // that make sense for the entry-point service — courier stays courier, cab
  // opens the full mobility strip.
  const VEHICLE_STRIP = ["bike", "ride", "car", "express"];   // covers Bike · Mini · Sedan · Auto
  const isCabLike = ["bike", "ride", "car", "express", "gifts"].includes(service);
  const services = isCabLike ? VEHICLE_STRIP : [service];
  const VEHICLE_META = {
    bike:    { icon: "🏍",  label: "Bike",   sub: "1 seat"   },
    ride:    { icon: "🛺",  label: "Auto",   sub: "3 seats"  },
    car:     { icon: "🚗",  label: "Mini",   sub: "4 seats"  },
    express: { icon: "🚕",  label: "Sedan",  sub: "4 · AC"   },
    food:    { icon: "🍔",  label: "Food",   sub: ""         },
    mart:    { icon: "🛒",  label: "Mart",   sub: ""         },
    gifts:   { icon: "🎁",  label: "Gifts",  sub: ""         },
  };

  // Load saved places on mount (silent if unauth)
  useEffect(() => {
    api.get("/places/saved").then(r => setSaved(r.data?.items || [])).catch(() => setSaved([]));
  }, []);

  // Slice C1 · when pickup+dropoff are set, fetch quotes for every vehicle in
  // the strip in a single round-trip so the user sees a Grab/Uber-style
  // carousel with fare + ETA per vehicle without N sequential calls.
  useEffect(() => {
    if (!pickup || !dropoff) { setQuote(null); setMultiQuotes([]); return; }
    const validStops = stops.filter(s => s && s.lat != null && s.lng != null);
    const t = setTimeout(async () => {
      try {
        if (services.length > 1) {
          const r = await api.post("/rides/multi-quote", {
            services,
            pickup_lat:  pickup.lat,  pickup_lng:  pickup.lng,
            dropoff_lat: dropoff.lat, dropoff_lng: dropoff.lng,
            stops: validStops.map(s => ({ lat: s.lat, lng: s.lng, address: s.label })),
          });
          setMultiQuotes(r.data?.quotes || []);
          const active = (r.data?.quotes || []).find(q => q.service === selectedSvc) || r.data?.quotes?.[0];
          if (active) setQuote(active);
        } else {
          const r = await api.post("/delivery/quote", {
            service: selectedSvc,
            pickup_lat:  pickup.lat,  pickup_lng:  pickup.lng,
            dropoff_lat: dropoff.lat, dropoff_lng: dropoff.lng,
            stops: validStops.map(s => ({ lat: s.lat, lng: s.lng, address: s.label })),
          });
          setQuote(r.data);
        }
      } catch { setQuote(null); setMultiQuotes([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [pickup, dropoff, stops, selectedSvc]);   // eslint-disable-line react-hooks/exhaustive-deps

  const confirm = async () => {
    if (!pickup || !dropoff || !quote) return;
    // META-1 · Non-cash payments go through the FindPay drawer FIRST;
    // the actual /delivery booking is created inside the onSuccess callback
    // so the driver only sees a paid ride. Cash stays optimistic.
    if (payMethod !== "cash") {
      try { localStorage.setItem("f2n_pay_pref", payMethod); } catch { /* ignore */ }
      if (!platformMid) { setError("Payment service warming up — try again in a moment."); return; }
      setPayOpen(true);
      return;
    }
    return finalizeBooking();
  };

  const finalizeBooking = async (paymentTxn = null) => {
    setConf(true); setError(null);
    try { localStorage.setItem("f2n_pay_pref", payMethod); } catch { /* ignore */ }
    const validStops = stops.filter(s => s && s.lat != null && s.lng != null);
    try {
      const r = await api.post("/delivery", {
        recipient_name: "Me",
        recipient_phone: "0",
        pickup_lat:  pickup.lat,  pickup_lng:  pickup.lng,  pickup_address:  pickup.label,
        dropoff_lat: dropoff.lat, dropoff_lng: dropoff.lng, dropoff_address: dropoff.label,
        stops: validStops.map(s => ({ lat: s.lat, lng: s.lng, address: s.label })),
        service: selectedSvc,
        scheduled_for: scheduleOn && scheduleAt ? new Date(scheduleAt).toISOString() : undefined,
        package_desc: `${selectedSvc} trip · ₹${quote.fare_inr} · pay=${payMethod}${paymentTxn ? ` · findpay=${paymentTxn.txn_id}` : ""}${quote.surge_multiplier > 1 ? ` (${quote.surge_multiplier}× surge)` : ""}${validStops.length ? ` · +${validStops.length} stop${validStops.length>1?"s":""}` : ""}${scheduleOn && scheduleAt ? ` · scheduled ${new Date(scheduleAt).toLocaleString()}` : ""}`,
      });
      navigate(`/ride/${r.data.delivery_id}`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Booking failed. Please sign in.");
    } finally { setConf(false); }
  };

  const applySaved = (p) => {
    const val = { label: p.address || p.name, lat: p.lat, lng: p.lng };
    if (assignTo === "dropoff") setDropoff(val);
    else                        setPickup(val);
    setAssignTo(null);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[9500] grid place-items-end md:place-items-center p-0 md:p-6" onClick={onClose} data-testid="ride-book-sheet">
      <div className="bg-white w-full md:max-w-md rounded-t-2xl md:rounded-2xl p-5 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="heading font-bold text-lg text-stone-900 capitalize">{service} trip</div>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-stone-100" aria-label="Close"><ChevronRight size={16} className="rotate-90 text-stone-500" /></button>
        </div>

        {/* Saved-places quick fill */}
        {saved.length > 0 && (
          <div className="mb-3" data-testid="fare-saved-strip">
            <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">
              Quick fill{assignTo ? ` → ${assignTo}` : ""}
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {saved.map(p => (
                <button
                  key={p.place_id}
                  onClick={() => applySaved(p)}
                  className="text-xs font-bold px-3 py-1.5 rounded-full border bg-white border-stone-200 hover:border-stone-300 capitalize"
                  data-testid={`fare-saved-${p.label}`}
                  onMouseEnter={() => { if (!assignTo && !pickup) setAssignTo("pickup"); }}
                >
                  {p.label === "home" ? "🏠 " : p.label === "work" ? "🏢 " : "📍 "}
                  {p.name || p.label}
                </button>
              ))}
              <button onClick={() => setAssignTo(assignTo === "pickup" ? "dropoff" : "pickup")} className="text-[10px] font-bold text-stone-400 underline">
                fill {assignTo === "pickup" ? "dropoff" : "pickup"}
              </button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <AddressField label="Pickup"  value={pickup}  onChange={setPickup}  testid="fare-pickup" />
          {stops.map((s, i) => (
            <div key={i} className="flex items-center gap-1" data-testid={`fare-stop-row-${i}`}>
              <div className="flex-1">
                <AddressField label={`Stop ${i+1}`} value={s} onChange={(v) => {
                  const next = [...stops]; next[i] = v; setStops(next);
                }} testid={`fare-stop-${i}`} />
              </div>
              <button
                onClick={() => setStops(stops.filter((_, k) => k !== i))}
                className="mt-4 p-1 rounded-full hover:bg-stone-100 text-rose-600"
                data-testid={`fare-stop-del-${i}`}
                aria-label="Remove stop"
              ><X size={14} /></button>
            </div>
          ))}
          <button
            onClick={() => setStops([...stops, null])}
            className="text-xs font-bold text-emerald-700 inline-flex items-center gap-1"
            data-testid="fare-add-stop"
          >
            <Plus size={12} /> Add stop
          </button>
          <AddressField label="Dropoff" value={dropoff} onChange={setDropoff} testid="fare-dropoff" />
        </div>
        {/* Slice C1 · pin-drag map toggle — Grab/Uber-style fine-tune. */}
        <div className="mt-2 flex items-center gap-1.5" data-testid="pindrag-toggle-row">
          <span className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mr-1">Fine-tune on map</span>
          <button
            type="button"
            onClick={() => setMapMode(mapMode === "pickup" ? null : "pickup")}
            className={`text-[11px] font-bold rounded-full px-2.5 py-1 border ${
              mapMode === "pickup" ? "bg-emerald-600 border-emerald-700 text-white" : "bg-white border-stone-200 text-stone-700 hover:border-stone-300"
            }`}
            data-testid="pindrag-toggle-pickup"
          >
            📍 Pickup
          </button>
          <button
            type="button"
            onClick={() => setMapMode(mapMode === "dropoff" ? null : "dropoff")}
            className={`text-[11px] font-bold rounded-full px-2.5 py-1 border ${
              mapMode === "dropoff" ? "bg-emerald-600 border-emerald-700 text-white" : "bg-white border-stone-200 text-stone-700 hover:border-stone-300"
            }`}
            data-testid="pindrag-toggle-dropoff"
          >
            🏁 Dropoff
          </button>
        </div>
        {mapMode === "pickup" && (
          <div className="mt-2" data-testid="pindrag-wrap-pickup">
            <PinDragMap
              initial={pickup}
              onCommit={setPickup}
              label="Pickup"
              height="180px"
            />
          </div>
        )}
        {mapMode === "dropoff" && (
          <div className="mt-2" data-testid="pindrag-wrap-dropoff">
            <PinDragMap
              initial={dropoff}
              onCommit={setDropoff}
              label="Dropoff"
              height="180px"
            />
          </div>
        )}
        {quote && (
          <div className="mt-3 p-3 rounded-xl border border-emerald-200 bg-emerald-50" data-testid="fare-quote">
            <div className="flex items-baseline justify-between">
              <div className="heading font-black text-2xl text-emerald-800">₹{quote.fare_inr.toLocaleString("en-IN")}</div>
              {quote.surge_multiplier > 1 && (
                <div className="text-[10px] font-bold text-rose-700 bg-rose-100 px-2 py-0.5 rounded-full" data-testid="fare-surge">
                  {quote.surge_multiplier}× surge
                </div>
              )}
            </div>
            <div className="text-xs text-emerald-700 mt-1">
              {quote.distance_km} km · ETA ~{quote.eta_minutes} min · base ₹{quote.breakdown.base} + ₹{quote.breakdown.per_km_effective}/km
              {quote.stops_count > 0 && <> · +₹{quote.breakdown.stops_fee} for {quote.stops_count} stop{quote.stops_count>1?"s":""}</>}
            </div>
            {/* Slice C1 · fare-breakdown accordion — the "how is my fare computed" drawer */}
            <button
              type="button"
              onClick={() => setBreakdownOpen(v => !v)}
              className="mt-2 text-[11px] font-bold text-emerald-800 hover:text-emerald-900 inline-flex items-center gap-1"
              data-testid="fare-breakdown-toggle"
            >
              {breakdownOpen ? "▾" : "▸"} How is my fare computed?
            </button>
            {breakdownOpen && (
              <div className="mt-1.5 rounded-lg bg-white border border-emerald-100 p-2.5 space-y-1 text-[11px] text-stone-700" data-testid="fare-breakdown-drawer">
                <div className="flex justify-between"><span>Base fare</span><span className="tabular-nums font-semibold">₹{quote.breakdown.base}</span></div>
                <div className="flex justify-between">
                  <span>Distance · {quote.breakdown.distance_km} km × ₹{quote.breakdown.per_km_effective}/km</span>
                  <span className="tabular-nums font-semibold">₹{Math.round(quote.breakdown.distance_km * quote.breakdown.per_km_effective)}</span>
                </div>
                {quote.breakdown.stops_fee > 0 && (
                  <div className="flex justify-between"><span>Stops · {quote.stops_count} × ₹15</span><span className="tabular-nums font-semibold">₹{quote.breakdown.stops_fee}</span></div>
                )}
                {quote.surge_multiplier > 1 && (
                  <div className="flex justify-between text-rose-700"><span>Surge · {quote.surge_multiplier}×</span><span className="tabular-nums font-semibold">included in km rate</span></div>
                )}
                <div className="border-t border-emerald-100 pt-1 flex justify-between font-bold text-emerald-800">
                  <span>Total</span><span className="tabular-nums">₹{quote.fare_inr}</span>
                </div>
                {quote.surge_multiplier > 1 && (
                  <div className="text-[10px] text-stone-500 pt-0.5">
                    Higher than usual — {quote.supply_demand?.pending_5km || 0} rider{quote.supply_demand?.pending_5km === 1 ? "" : "s"} waiting for {quote.supply_demand?.providers_5km || 0} driver{quote.supply_demand?.providers_5km === 1 ? "" : "s"} within 5 km.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Slice C1 · vehicle carousel — appears once we have quotes for
            multiple vehicles. Horizontal-scroll, tap to switch service. */}
        {isCabLike && multiQuotes.length > 1 && (
          <div className="mt-3 -mx-1 overflow-x-auto scroll-smooth" data-testid="vehicle-carousel">
            <div className="flex gap-2 px-1 pb-1 snap-x">
              {multiQuotes.map(q => {
                const meta = VEHICLE_META[q.service] || { icon: "🚕", label: q.service, sub: "" };
                const active = q.service === selectedSvc;
                return (
                  <button
                    key={q.service}
                    onClick={() => { setSelectedSvc(q.service); setQuote(q); }}
                    className={`snap-start shrink-0 w-[92px] rounded-xl border-2 p-2 text-left transition-colors ${
                      active
                        ? "border-emerald-600 bg-emerald-50"
                        : "border-stone-200 bg-white hover:border-stone-300"
                    }`}
                    data-testid={`vehicle-chip-${q.service}`}
                  >
                    <div className="text-lg leading-none">{meta.icon}</div>
                    <div className="text-[11px] font-bold text-stone-900 mt-0.5">{meta.label}</div>
                    <div className="text-[9px] text-stone-500">{meta.sub}</div>
                    <div className="text-xs font-black text-stone-900 mt-0.5 tabular-nums">₹{q.fare_inr}</div>
                    <div className="text-[9px] text-stone-500">~{q.eta_minutes} min</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Slice C1 · payment method row — visible BEFORE confirm.
            Preference is persisted so the user picks once. */}
        {quote && (
          <div className="mt-3 flex items-center gap-1.5 flex-wrap" data-testid="pay-method-row">
            <span className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mr-1">Pay with</span>
            {["upi", "card", "wallet", "cash"].map(m => (
              <button
                key={m}
                onClick={() => setPayMethod(m)}
                className={`text-[11px] font-bold rounded-full px-2.5 py-1 border ${
                  payMethod === m
                    ? "bg-[#0F5132] border-[#0F5132] text-white"
                    : "bg-white border-stone-200 text-stone-700 hover:border-stone-300"
                }`}
                data-testid={`pay-${m}`}
              >
                {m === "upi" ? "UPI" : m === "card" ? "Card" : m === "wallet" ? "Wallet" : "Cash"}
              </button>
            ))}
          </div>
        )}
        {error && <div className="mt-2 text-xs text-red-700">{error}</div>}

        {/* Schedule for later */}
        <div className="mt-3 flex items-center justify-between gap-2 bg-stone-50 border border-stone-200 rounded-xl px-3 py-2">
          <label className="text-xs font-bold text-stone-700 inline-flex items-center gap-2 select-none">
            <input
              type="checkbox"
              checked={scheduleOn}
              onChange={e => setSchedOn(e.target.checked)}
              className="accent-emerald-700"
              data-testid="fare-schedule-toggle"
            />
            Book for later
          </label>
          {scheduleOn && (
            <input
              type="datetime-local"
              value={scheduleAt}
              onChange={e => setSchedAt(e.target.value)}
              className="text-xs border border-stone-200 rounded-lg px-2 py-1"
              data-testid="fare-schedule-at"
              min={new Date(Date.now() + 5 * 60_000).toISOString().slice(0,16)}
            />
          )}
        </div>

        <button
          onClick={confirm}
          disabled={!quote || confirming || (scheduleOn && !scheduleAt)}
          className="mt-3 w-full rounded-full bg-[#0F5132] hover:bg-[#0a3d24] text-white font-bold py-3 disabled:opacity-50"
          data-testid="fare-confirm"
        >
          {confirming ? "Booking…" : quote
              ? (scheduleOn && scheduleAt ? `Schedule ${selectedSvc} · ₹${quote.fare_inr}` : `Confirm ${selectedSvc} · ₹${quote.fare_inr}${payMethod !== "cash" ? ` · ${payMethod.toUpperCase()}` : ""}`)
              : "Enter pickup & dropoff"}
        </button>
      </div>
      {/* META-1 · FindPay drawer for non-cash payments. Booking is finalized on payment success. */}
      {payOpen && quote && platformMid && (
        <FindPayCheckout
          open={payOpen}
          onOpenChange={setPayOpen}
          merchantId={platformMid}
          amountPaise={Math.round((quote.fare_inr || 0) * 100)}
          currency="INR"
          description={`${selectedSvc} · ${pickup?.label || "Pickup"} → ${dropoff?.label || "Dropoff"}`}
          context={{ kind: "cab_prebook", service: selectedSvc }}
          onSuccess={(txn) => { setPayOpen(false); finalizeBooking(txn); }}
        />
      )}
    </div>
  );
}

function AddressField({ label, value, onChange, testid }) {
  const [q, setQ] = useState(value?.label || "");
  const [opts, setOpts] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!q || q.length < 3) { setOpts([]); return; }
    const h = setTimeout(async () => {
      try {
        const r = await api.get("/geocode/suggest", { params: { q, limit: 5 } });
        setOpts(r.data?.items || r.data?.results || []);
        setOpen(true);
      } catch { setOpts([]); }
    }, 300);
    return () => clearTimeout(h);
  }, [q]);
  return (
    <div className="relative">
      <div className="text-[10px] uppercase tracking-widest font-bold text-stone-500 mb-1">{label}</div>
      <input
        value={q}
        onChange={e => { setQ(e.target.value); onChange(null); }}
        placeholder={`Search ${label.toLowerCase()}…`}
        className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm"
        data-testid={testid}
      />
      {open && opts.length > 0 && !value && (
        <div className="absolute inset-x-0 top-full mt-1 bg-white border border-stone-200 rounded-lg shadow-md max-h-60 overflow-y-auto z-10">
          {opts.map((o, i) => (
            <button
              key={i}
              onClick={() => { onChange({ label: o.label || o.display_name || o.name, lat: o.lat, lng: o.lng || o.lon }); setQ(o.label || o.display_name || o.name); setOpen(false); }}
              className="w-full text-left px-3 py-2 text-xs hover:bg-stone-50 border-b border-stone-100 last:border-0"
              data-testid={`${testid}-opt-${i}`}
            >
              {o.label || o.display_name || o.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ActiveDeliveryCard() {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/delivery/mine?role=sender&limit=5")
      .then(r => {
        const active = (r.data || []).find(x => ["requested", "assigned", "picked_up", "in_transit"].includes(x.status));
        setD(active || null);
      }).catch(() => setD(null));
  }, []);
  if (!d) return null;
  return (
    <Link
      to={`/ride/${d.delivery_id}`}
      className="flex items-center gap-3 bg-white border border-stone-200 rounded-2xl p-3 shadow-sm hover:border-stone-300"
      data-testid="superapp-active-delivery"
    >
      <div className="w-10 h-10 rounded-full bg-orange-100 grid place-items-center">
        <Truck size={16} className="text-orange-600" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-stone-900 text-sm truncate">
          {d.status === "requested" ? "Finding a driver…" : d.status === "assigned" ? "Driver assigned" : d.status === "picked_up" ? "Picked up" : "In transit"}
        </div>
        <div className="text-xs text-stone-500 truncate">
          {d.dropoff?.address || d.package_desc || "Track your trip"}
        </div>
      </div>
      <div className="text-[10px] font-bold text-emerald-700 uppercase tracking-widest">Track →</div>
    </Link>
  );
}


const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function RecurringRides() {
  const [items, setItems]     = useState([]);
  const [open, setOpen]       = useState(false);
  const [form, setForm]       = useState({
    label: "Commute", service: "ride",
    pickup: null, dropoff: null,
    time_of_day: "08:00",
    days: [0,1,2,3,4],
  });
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState(null);
  const load = () => api.get("/rides/recurring").then(r => setItems(r.data?.items || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.pickup || !form.dropoff) { setErr("Pickup & dropoff required"); return; }
    setBusy(true); setErr(null);
    try {
      await api.post("/rides/recurring", {
        label: form.label, service: form.service,
        pickup_lat: form.pickup.lat, pickup_lng: form.pickup.lng, pickup_address: form.pickup.label,
        dropoff_lat: form.dropoff.lat, dropoff_lng: form.dropoff.lng, dropoff_address: form.dropoff.label,
        time_of_day: form.time_of_day, days: form.days,
      });
      setOpen(false); load();
    } catch (e) { setErr(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const spawnAndOpen = async (recur_id) => {
    try {
      const r = await api.post(`/rides/recurring/${recur_id}/spawn-due`);
      if (r.data?.delivery_id) window.location.href = `/ride/${r.data.delivery_id}`;
    } catch { /* toast */ }
  };
  const remove = async (recur_id) => { await api.delete(`/rides/recurring/${recur_id}`); load(); };

  return (
    <div className="mt-4 bg-white border border-stone-200 rounded-2xl p-4" data-testid="recurring-rides">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-widest font-bold text-stone-500">Recurring rides</div>
        <button onClick={() => setOpen(o => !o)} className="text-xs font-bold text-emerald-700 inline-flex items-center gap-1" data-testid="recurring-toggle">
          <Plus size={12} /> {open ? "Cancel" : "New"}
        </button>
      </div>
      {items.length === 0 && !open && (
        <div className="text-xs text-stone-500">
          No recurring rides yet — set a daily commute so mornings run themselves.
        </div>
      )}
      {items.map(it => {
        const paused = it.paused_until && it.paused_until > new Date().toISOString();
        return (
          <div key={it.recur_id} className="flex items-center gap-2 py-2 border-b border-stone-100 last:border-0" data-testid={`recurring-item-${it.recur_id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-stone-900 text-sm truncate">
                {it.label} · {it.service}
                {paused && <span className="ml-2 text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">Paused</span>}
              </div>
              <div className="text-[11px] text-stone-500 truncate">
                {it.time_of_day} · {it.days.map(d => DAY_NAMES[d]).join(",")} · {it.pickup?.address} → {it.dropoff?.address}
                {paused && <span className="ml-1">· resumes {new Date(it.paused_until).toLocaleDateString()}</span>}
              </div>
            </div>
            <button
              onClick={async () => {
                await api.post(`/rides/recurring/${it.recur_id}/pause`, { days: paused ? 0 : 7 });
                load();
              }}
              className={`text-[11px] font-bold rounded-full px-2.5 py-1 ${paused ? "bg-amber-500 text-white" : "border border-amber-300 text-amber-700 bg-amber-50"}`}
              data-testid={`recurring-pause-${it.recur_id}`}
            >
              {paused ? "Resume" : "Pause 1 wk"}
            </button>
            <button onClick={() => spawnAndOpen(it.recur_id)} disabled={paused} className="text-[11px] font-bold rounded-full bg-emerald-600 text-white px-2.5 py-1 disabled:opacity-40" data-testid={`recurring-spawn-${it.recur_id}`}>Book next</button>
            <button onClick={() => remove(it.recur_id)} className="text-[11px] rounded-full border border-stone-200 text-stone-500 px-2 py-1" data-testid={`recurring-del-${it.recur_id}`}><X size={11} /></button>
          </div>
        );
      })}
      {open && (
        <div className="mt-3 space-y-2 border-t border-stone-100 pt-3">
          <input value={form.label} onChange={e => setForm({...form, label: e.target.value})} placeholder="Label · e.g. Morning commute" className="w-full text-sm border border-stone-200 rounded-lg px-3 py-2" data-testid="recurring-label" />
          <div className="flex gap-2 items-center">
            <select value={form.service} onChange={e => setForm({...form, service: e.target.value})} className="text-sm border border-stone-200 rounded-lg px-2 py-2 flex-1" data-testid="recurring-service">
              {["ride","car","bike"].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <input type="time" value={form.time_of_day} onChange={e => setForm({...form, time_of_day: e.target.value})} className="text-sm border border-stone-200 rounded-lg px-2 py-2" data-testid="recurring-time" />
          </div>
          <div className="flex gap-1">
            {DAY_NAMES.map((n, i) => (
              <button key={n} onClick={() => setForm({...form, days: form.days.includes(i) ? form.days.filter(d => d!==i) : [...form.days, i]})}
                className={`text-[10px] font-bold w-8 h-8 rounded-full ${form.days.includes(i) ? "bg-emerald-600 text-white" : "bg-stone-100 text-stone-500"}`}
                data-testid={`recurring-day-${i}`}
              >{n}</button>
            ))}
          </div>
          <AddressField label="Pickup"  value={form.pickup}  onChange={(v) => setForm({...form, pickup: v})}  testid="recurring-pickup" />
          <AddressField label="Dropoff" value={form.dropoff} onChange={(v) => setForm({...form, dropoff: v})} testid="recurring-dropoff" />
          {err && <div className="text-xs text-red-700">{err}</div>}
          <button onClick={submit} disabled={busy} className="w-full rounded-full bg-[#0F5132] text-white font-bold py-2 text-sm disabled:opacity-50" data-testid="recurring-save">
            {busy ? "Saving…" : "Save recurring ride"}
          </button>
        </div>
      )}
    </div>
  );
}



function TopupNudge() {
  const [alert, setAlert] = useState(null);
  useEffect(() => {
    const run = async () => {
      try {
        const [me, w] = await Promise.all([
          api.get("/delivery/mine?role=sender&limit=5"),
          api.get("/wallet"),
        ]);
        const scheduled = (me.data || []).find(d => d.status === "scheduled" && d.scheduled_for);
        if (!scheduled) return setAlert(null);
        const dueMs = new Date(scheduled.scheduled_for).getTime() - Date.now();
        if (dueMs < 0 || dueMs > 30 * 60 * 1000) return setAlert(null);
        // Estimate fare quickly
        const q = await api.post("/delivery/quote", {
          service: scheduled.service || "ride",
          pickup_lat:  scheduled.pickup?.lat, pickup_lng:  scheduled.pickup?.lng,
          dropoff_lat: scheduled.dropoff?.lat, dropoff_lng: scheduled.dropoff?.lng,
        });
        const balanceInr = Math.floor(((w.data?.balance_paise || 0) + (w.data?.free_credit_paise || 0)) / 100);
        if (balanceInr < q.data.fare_inr) {
          setAlert({
            delivery_id: scheduled.delivery_id,
            fare: q.data.fare_inr, balance: balanceInr,
            due_in_min: Math.round(dueMs / 60000),
          });
        }
      } catch { /* silent */ }
    };
    run();
    const t = setInterval(run, 60_000);
    return () => clearInterval(t);
  }, []);
  if (!alert) return null;
  return (
    <div className="mt-4 bg-amber-50 border border-amber-200 rounded-2xl p-4" data-testid="topup-nudge">
      <div className="text-[11px] uppercase tracking-widest font-bold text-amber-700 mb-1">
        Trip in {alert.due_in_min} min
      </div>
      <div className="font-bold text-stone-900">
        Your wallet is short by ₹{alert.fare - alert.balance}
      </div>
      <div className="text-xs text-stone-600 mt-1">
        Estimated fare ₹{alert.fare} · wallet ₹{alert.balance}. Top up now so your driver isn't delayed.
      </div>
      <Link to="/wallet" className="mt-3 inline-flex rounded-full bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold px-4 py-2" data-testid="topup-nudge-cta">
        Top up wallet →
      </Link>
    </div>
  );
}



function WeeklySummary() {
  const [data, setData]     = useState(null);
  const [open, setOpen]     = useState(false);

  useEffect(() => {
    let cancelled = false;
    try {
      const wantsModal = new URLSearchParams(window.location.search).get("summary") === "1";
      if (!wantsModal) return;
      (async () => {
        try {
          const r = await api.get("/rides/summary/weekly");
          if (cancelled) return;
          setData(r.data || null);
          setOpen(true);
        } catch { /* ignore */ }
      })();
    } catch { /* ignore */ }
    return () => { cancelled = true; };
  }, []);

  if (!open || !data || (data.trips || 0) === 0) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[9800] grid place-items-end sm:place-items-center p-4"
      onClick={() => setOpen(false)}
      data-testid="weekly-summary-modal"
    >
      <div
        className="bg-white rounded-3xl sm:rounded-2xl p-6 max-w-md w-full"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="heading font-black text-xl text-stone-900">This week on Find2Near</div>
          <button
            onClick={() => setOpen(false)}
            className="p-1 rounded-full hover:bg-stone-100"
            data-testid="weekly-summary-close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-xl bg-emerald-50 p-3">
            <div className="text-[10px] font-bold uppercase text-emerald-800">Trips</div>
            <div className="text-2xl font-black text-emerald-900" data-testid="weekly-trips">{data.trips}</div>
          </div>
          <div className="rounded-xl bg-sky-50 p-3">
            <div className="text-[10px] font-bold uppercase text-sky-800">Spent</div>
            <div className="text-2xl font-black text-sky-900" data-testid="weekly-spent">₹{data.spent_inr}</div>
          </div>
          <div className="rounded-xl bg-amber-50 p-3">
            <div className="text-[10px] font-bold uppercase text-amber-800">Distance</div>
            <div className="text-2xl font-black text-amber-900">{data.distance_km} km</div>
          </div>
        </div>
        {data.top_service && (
          <div className="mt-3 text-sm text-stone-700">
            Most used: <span className="font-bold capitalize">{data.top_service}</span>
          </div>
        )}
        {(data.recent || []).length > 0 && (
          <div className="mt-4">
            <div className="text-xs font-bold uppercase text-stone-500 mb-1">Recent trips</div>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {data.recent.map(t => (
                <Link
                  key={t.delivery_id}
                  to={`/ride/${t.delivery_id}`}
                  className="flex items-center justify-between rounded-lg hover:bg-stone-50 px-2 py-1.5 text-xs"
                >
                  <span className="capitalize text-stone-700">{t.service || "ride"}</span>
                  <span className="text-stone-500">
                    {t.distance_km ? `${t.distance_km.toFixed?.(1) || t.distance_km} km · ` : ""}₹{t.fare_inr || 0}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
